package one.healthium.tasks.data

import co.touchlab.kermit.Logger
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.shareIn
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format
import kotlinx.datetime.toLocalDateTime
import one.healthium.base.preferences.AppPreferences
import one.healthium.db.dao.DailyTasksDao
import one.healthium.tasks.data.mapper.DailyTaskMapper
import one.healthium.tasks.data.mapper.name
import one.healthium.tasks.data.storages.TasksInfoStorage
import one.healthium.tasks.domain.DailyTaskModel
import one.healthium.tasks.domain.DailyTaskType
import one.healthium.tasks.domain.DailyTasksRepository
import kotlin.time.Clock

internal class AppDailyTasksRepository(
    private val dailyTaskMapper: DailyTaskMapper,
    private val appPreferences: AppPreferences,
    private val tasksInfoStorage: TasksInfoStorage,
    private val dailyTasksDao: DailyTasksDao,
) : DailyTasksRepository {

    private val scope = CoroutineScope(Dispatchers.Default + SupervisorJob())

    private val dailyTasksFlow = dailyTasksDao.getDailyTaskByDate(getTodayDate())
        .map { it.mapNotNull(dailyTaskMapper::entityToModel) }
        .onEach {
            if (it.isEmpty()) {
                refreshTodayDailyTasks()
            }
        }
        .shareIn(scope, started = SharingStarted.WhileSubscribed(), replay = 1)

    override fun getDailyTasks(): Flow<List<DailyTaskModel>> {
        return dailyTasksFlow
    }

    override suspend fun completeTaskById(id: String) {
        dailyTasksDao.incrementCompletionCount(id)
    }

    override suspend fun completeTodayTaskByType(type: DailyTaskType) {
        val dailyTasks = dailyTasksDao.getDailyTaskByDate(getTodayDate()).first()
        val task = dailyTasks.firstOrNull {
            if (type is DailyTaskType.ReadInsight) {
                it.type == type.name() && it.payload == type.id
            } else if (type is DailyTaskType.DoWorkout) {
                it.type == type.name() && it.payload == type.id
            } else {
                it.type == type.name()
            }
        }
        if (task != null) {
            dailyTasksDao.incrementCompletionCount(task.id)
        } else {
            Logger.d("Task not found for type: $type, tasks: $dailyTasks")
        }
    }

    override fun getDailyTasksUnread(): Flow<Boolean> {
        return appPreferences.dailyTaskUnreadFlow
    }

    override suspend fun markDailyTasksAsRead() {
        appPreferences.dailyTaskUnread = false
    }

    private suspend fun refreshTodayDailyTasks() {
        val dailyTasks = tasksInfoStorage.getTasksInfo()
        if (dailyTasks.isEmpty()) {
            return
        }
        var dayIndex = appPreferences.dailyTaskDayIndex
        if (dayIndex >= dailyTasks.size) {
            appPreferences.dailyTaskDayIndex = 0
            dayIndex = 0
        } else {
            appPreferences.dailyTaskDayIndex = dayIndex + 1
        }

        val dailyTasksForToday = dailyTasks[dayIndex]
        val dailyTasksEntities = dailyTaskMapper.createInfoDtos(dailyTasksForToday, getTodayDate())

        dailyTasksDao.insertDailyTasks(dailyTasksEntities)
        appPreferences.dailyTaskUnread = true
    }

    private fun getTodayDate() = Clock.System
        .now()
        .toLocalDateTime(TimeZone.currentSystemDefault())
        .date
        .format(LocalDate.Formats.ISO)
}
